import { useState, useEffect } from 'react';
import { useGlobal } from '@/context/GlobalContext';
import { usePageContent } from '@/hooks/usePageContent';
import { SampleProducts, ProductCard } from '@/components/SampleProducts';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import SEO, { serviceSchema } from '@/components/SEO';
import { 
  Calendar, Radio, Plane, Factory, TreePine, Building, CheckCircle,
  ClipboardList, Settings, Wrench, Globe, ArrowRight
} from 'lucide-react';
import axios from 'axios';

const UltrasonicPage = () => {
  const { settings, API } = useGlobal();
  const { content, loading: contentLoading } = usePageContent('ultrasonic');
  const [ultrasonicModels, setUltrasonicModels] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/ultrasonic-models`);
        setUltrasonicModels(response.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [API]);

  // Get content with defaults
  const hero = content.hero || {};
  const whyConsulting = content.why_consulting || {};
  const ourApproachCMS = content.our_approach || {};
  const applicationsCMS = content.applications || {};
  const systemOptionsCMS = content.system_options || {};
  const benefitsCMS = content.benefits || {};
  const cta = content.cta || {};

  // Default values
  const defaults = {
    hero: {
      badge: "CONSULTING & DISTRIBUTION",
      headline: "Ultrasonic Animal Deterrence",
      description: "Humane bird and animal deterrence for airports, heritage sites, warehouses, and power facilities. We consult, configure, and supply.",
      highlight_badge: "Consulting & Distribution Partner",
      background_image: "https://static.prod-images.emergentagent.com/jobs/a9e4d36a-e5ff-43e1-ab5f-86fa29a8738a/images/fb4621523935682b807ebb18d668fa3c1a3503446c7e16b79941bfe3b89eda54.png"
    },
    why_consulting: {
      title: "Why Consulting Matters",
      paragraphs: [
        "Generic solutions don't work. Every site has unique challenges — bird species, migration patterns, building layouts, environmental conditions.",
        "A poorly configured system wastes money and doesn't solve the problem. That's why we start with assessment, not sales."
      ],
      stats: [
        { value: "15-25 kHz", label: "Frequency Range" },
        { value: "Up to 300m", label: "Coverage Range" }
      ]
    },
    our_approach: {
      title: "Our Approach",
      description: "We don't just sell devices — we design effective deterrence systems.",
      steps: [
        { icon: 'ClipboardList', step: '1', title: 'Site Assessment', description: 'We evaluate your premises — coverage area, animal types, environmental factors, existing infrastructure.' },
        { icon: 'Settings', step: '2', title: 'Custom Configuration', description: 'No two sites are the same. We design a system layout optimized for your specific conditions.' },
        { icon: 'Wrench', step: '3', title: 'Supply & Support', description: 'We provide the equipment and ongoing technical support to ensure long-term effectiveness.' }
      ]
    },
    applications: {
      title: "Application Scenarios",
      description: "Different environments require different approaches.",
      items: [
        { icon: 'Plane', title: 'Aviation', description: 'Runway and apron bird strike prevention', examples: 'Airports, helipads', image: 'https://images.unsplash.com/photo-1545460463-afdcfb3a3e53?w=400&q=80' },
        { icon: 'Factory', title: 'Industrial', description: 'Protect equipment and facilities', examples: 'Power stations, substations, manufacturing', image: 'https://images.unsplash.com/photo-1554050546-c125a25df013?w=400&q=80' },
        { icon: 'TreePine', title: 'Agriculture', description: 'Crop and orchard protection', examples: 'Farms, vineyards, fisheries', image: 'https://images.unsplash.com/photo-1760372057378-e8d6bda295eb?w=400&q=80' },
        { icon: 'Building', title: 'Heritage & Commercial', description: 'Protect buildings and inventory', examples: 'Warehouses, historic sites, monuments', image: 'https://images.unsplash.com/photo-1758789667762-56175fe4601c?w=400&q=80' }
      ]
    },
    system_options: {
      title: "System Options",
      description: "We configure the right combination based on your site assessment.",
      options: [
        { name: 'Single-Direction Systems', range: '150-200m coverage', use: 'Targeted protection for specific areas', ideal: 'Runways, linear perimeters' },
        { name: 'Multi-Direction Systems', range: '100-150m radius', use: '360° area protection', ideal: 'Warehouses, open fields' },
        { name: 'High-Power Industrial', range: '200-300m coverage', use: 'Large area, extended range', ideal: 'Power stations, large industrial sites' }
      ],
      note: "* Actual configuration depends on site assessment. These are representative options."
    },
    benefits: {
      title: "Key Benefits",
      items: ['Humane — no harm to animals', 'Environmentally friendly', 'Silent to humans', 'Low maintenance', 'Weather-resistant (IP67)', 'Adjustable frequencies', 'Solar power compatible', 'Remote monitoring available'],
      distribution: {
        badge: "GLOBAL DISTRIBUTION",
        title: "Available Worldwide",
        description: "We supply and support ultrasonic deterrence systems across Asia, Europe, the Americas, and beyond. Contact us for regional availability."
      }
    },
    cta: {
      title: "Let's Assess Your Site",
      description: "Tell us about your premises and the challenges you're facing. We'll recommend the right approach — no obligation."
    }
  };

  // Merge CMS content with defaults
  const h = { ...defaults.hero, ...hero };
  const wc = { ...defaults.why_consulting, ...whyConsulting };
  const oa = { ...defaults.our_approach, ...ourApproachCMS };
  const app = { ...defaults.applications, ...applicationsCMS };
  const so = { ...defaults.system_options, ...systemOptionsCMS };
  const ben = { ...defaults.benefits, ...benefitsCMS };
  const c = { ...defaults.cta, ...cta };

  const ourApproach = oa.steps || defaults.our_approach.steps;
  const applications = app.items || defaults.applications.items;
  const systemOptions = so.options || defaults.system_options.options;
  const benefits = ben.items || defaults.benefits.items;

  // Icon mapping
  const iconMap = { ClipboardList, Settings, Wrench, Plane, Factory, TreePine, Building };

  return (
    <div className="min-h-screen bg-[#1a1f2e]" data-testid="ultrasonic-page">
      <SEO 
        title="Ultrasonic Animal Deterrence - Bird Control Systems"
        description="Humane ultrasonic bird and animal deterrence. Consulting and exclusive distribution outside Greater China. Airport bird strike prevention, industrial facilities, heritage sites, agriculture."
        keywords="ultrasonic bird deterrent, animal deterrence system, airport bird control, humane bird repeller, industrial pest control, ultrasonic pest deterrent, animal hazard prevention, bird hazard control, wildlife hazard management, airport animal hazard"
        canonicalUrl="https://www.trinity-tw.com/ultrasonic"
        ogImage="https://static.prod-images.emergentagent.com/jobs/a9e4d36a-e5ff-43e1-ab5f-86fa29a8738a/images/fb4621523935682b807ebb18d668fa3c1a3503446c7e16b79941bfe3b89eda54.png"
        structuredData={serviceSchema('Ultrasonic Animal Deterrence', 'Consulting and distribution for humane ultrasonic bird and animal deterrence systems for airports, industrial facilities, heritage sites, and agriculture.')}
      />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 relative">
        {/* Background Image */}
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `url(${h.background_image})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1a1f2e]/30 via-[#1a1f2e]/50 to-[#1a1f2e]/60" />
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-[#1e2530] border border-[#3d4758] text-sm text-slate-300 mb-6">
            {h.badge}
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6 font-['Montserrat']">
            {h.headline}
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            {h.description}
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-6">
            <span className="px-4 py-2 rounded-full bg-[#5b3589]/20 border border-[#5b3589] text-sm text-[#a78bfa]">
              {h.highlight_badge}
            </span>
          </div>
        </div>
      </section>

      {/* Why Consulting Matters */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#141824]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-white mb-6 font-['Montserrat']">
                {wc.title}
              </h2>
              {(wc.paragraphs || defaults.why_consulting.paragraphs).map((para, idx) => (
                <p key={idx} className="text-slate-400 mb-4">{para}</p>
              ))}
              <div className="grid grid-cols-2 gap-4">
                {(wc.stats || defaults.why_consulting.stats).map((stat, idx) => (
                  <div key={idx} className="rounded-xl bg-[#232a3b] border border-[#3d4758] p-4">
                    <div className="text-2xl font-bold text-white font-['Montserrat']">{stat.value}</div>
                    <div className="text-slate-400 text-sm">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-center">
              <div className="relative">
                <div className="w-64 h-64 rounded-full bg-[#5b3589]/10 flex items-center justify-center">
                  <Radio className="w-24 h-24 text-[#5b3589]/50" />
                </div>
                {/* Animated rings effect */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-72 h-72 rounded-full border border-[#5b3589]/20 animate-ping" style={{animationDuration: '3s'}} />
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-80 h-80 rounded-full border border-[#5b3589]/10 animate-ping" style={{animationDuration: '4s'}} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4 font-['Montserrat']">
              {oa.title}
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              {oa.description}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {ourApproach.map((item, idx) => {
              const IconComponent = iconMap[item.icon] || ClipboardList;
              return (
                <div key={idx} className="rounded-2xl bg-[#232a3b] border border-[#3d4758] p-8">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-10 h-10 rounded-full bg-[#5b3589] flex items-center justify-center text-white font-bold">
                      {item.step}
                    </div>
                    <IconComponent className="w-6 h-6 text-[#8b5cf6]" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3 font-['Montserrat']">
                    {item.title}
                  </h3>
                  <p className="text-slate-400">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sample Ultrasonic Models */}
      <SampleProducts
        endpoint="ultrasonic-models"
        title="Sample Ultrasonic Systems"
        subtitle="Representative deterrence systems. Configuration depends on site assessment."
        limit={4}
        renderCard={(item) => (
          <ProductCard
            key={item.id}
            image={item.main_image}
            title={item.model_name}
            description={item.short_description}
            tags={[item.coverage_type, item.coverage_range].filter(Boolean)}
            Icon={Radio}
          />
        )}
      />

      {/* Applications */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#141824]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4 font-['Montserrat']">
              {app.title}
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              {app.description}
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {applications.map((appItem, idx) => {
              const IconComponent = iconMap[appItem.icon] || Plane;
              return (
                <div key={idx} className="rounded-2xl bg-[#232a3b] border border-[#3d4758] overflow-hidden hover:border-[#5b3589] transition-colors">
                  <div className="h-32 relative overflow-hidden">
                    <img 
                      src={appItem.image} 
                      alt={appItem.title}
                      className="w-full h-full object-cover opacity-60"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#151b23] to-transparent" />
                    <div className="absolute bottom-2 left-4">
                      <IconComponent className="w-6 h-6 text-[#8b5cf6]" />
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="text-lg font-semibold text-white mb-2 font-['Montserrat']">
                      {appItem.title}
                    </h3>
                    <p className="text-slate-400 text-sm mb-2">{appItem.description}</p>
                    <p className="text-slate-500 text-xs">{appItem.examples}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* System Options */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4 font-['Montserrat']">
              {so.title}
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              {so.description}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {systemOptions.map((option, idx) => (
              <div key={idx} className="rounded-2xl bg-[#232a3b] border border-[#3d4758] p-6">
                <h3 className="text-lg font-semibold text-white mb-4 font-['Montserrat']">
                  {option.name}
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Range</span>
                    <span className="text-slate-300">{option.range}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Best for</span>
                    <span className="text-slate-300">{option.use}</span>
                  </div>
                  <div className="pt-3 border-t border-[#3d4758]">
                    <span className="text-slate-500 text-xs">Ideal: {option.ideal}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <p className="text-center text-slate-500 text-sm mt-8">
            {so.note}
          </p>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#141824]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-white mb-8 font-['Montserrat']">
                {ben.title}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {benefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#8b5cf6] shrink-0" />
                    <span className="text-slate-300 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="rounded-2xl bg-[#232a3b] border border-[#3d4758] p-8">
              <div className="flex items-center gap-3 mb-4">
                <Globe className="w-6 h-6 text-[#8b5cf6]" />
                <span className="text-xs text-[#8b5cf6] font-medium">{(ben.distribution || defaults.benefits.distribution).badge}</span>
              </div>
              <h3 className="text-xl font-semibold text-white mb-4 font-['Montserrat']">
                {(ben.distribution || defaults.benefits.distribution).title}
              </h3>
              <p className="text-slate-400 mb-6">
                {(ben.distribution || defaults.benefits.distribution).description}
              </p>
              <Link to="/contact">
                <Button className="bg-[#5b3589] hover:bg-[#6d3d9e] text-white rounded-full">
                  Inquire About Your Region
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4 font-['Montserrat']">
            {c.title}
          </h2>
          <p className="text-slate-400 mb-8">
            {c.description}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href={settings.teams_scheduling_url} target="_blank" rel="noopener noreferrer">
              <Button className="bg-[#5b3589] hover:bg-[#6d3d9e] text-white rounded-full px-8">
                <Calendar className="w-4 h-4 mr-2" />
                Schedule a Consultation
              </Button>
            </a>
            <Link to="/contact">
              <Button variant="outline" className="border-slate-600 text-white hover:bg-slate-800 rounded-full px-8">
                Send Project Details
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default UltrasonicPage;
